import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TailSpin } from 'react-loader-spinner';  // Import spinner
import ImageModal from './ImageModal';  // Import Image Modal
import { toast } from 'react-hot-toast';
import DescriptionModal from './DescriptionModal';  // 👈 Import it


const calculatePrice = (product, goldRateIQD, usdToIqdRate) => {
  if (goldRateIQD && usdToIqdRate) {
    const calculatedPrice =
      ((product.weight * goldRateIQD + product.expense) / usdToIqdRate) + product.benefit;

    return calculatedPrice.toFixed(2);
  }
  return '...';
};

const AdminProductList = () => {
  const [goldRateIQD, setGoldRateIQD] = useState(0);
  const [usdToIqdRate, setUsdToIqdRate] = useState(0);

  useEffect(() => {
    axios.get('/api/settings')
      .then(res => {
        setGoldRateIQD(res.data.goldRateIQD);
        setUsdToIqdRate(res.data.usdToIqdRate);
      });
  }, []);

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [hideOutOfStock, setHideOutOfStock] = useState(false);
  const [savingRowId, setSavingRowId] = useState(null);
  const [message, setMessage] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [sortOption, setSortOption] = useState('new');  // Default sorting by date
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 10;  // Adjust as needed
  const [openModal, setOpenModal] = useState(false);  // State to control the Image Modal visibility
  const [selectedProduct, setSelectedProduct] = useState(null);  // State to hold the selected product for image management
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [descModalOpen, setDescModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    const res = await axios.get('/api/products/all');
    setProducts(res.data);
  };

  const handleOpenModal = (product) => {
    setSelectedProduct(product); // Set the selected product to be managed
    setOpenModal(true); // Open the modal
  };

  const handleCloseModal = () => {
    setOpenModal(false); // Close the modal
    setSelectedProduct(null); // Clear the selected product
  };

  // Handle product type filter change
  const handleProductTypeChange = (type) => {
    setSelectedProductTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Handle sort option change
  const handleSortChange = (option) => {
    setSortOption(option);
  };

  // Filter products based on search and filters
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(search.toLowerCase()) &&
    (!hideOutOfStock || product.stock > 0) &&
    (selectedProductTypes.length === 0 || selectedProductTypes.some(type =>
      product.productType && product.productType.includes(type)
    ))
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  const handleChange = (index, field, value) => {
    const updated = [...products];
    updated[index][field] = value;
    setProducts(updated);
  };

  const handleSave = async (product) => {
    try {
      setSavingRowId(product._id);
      await axios.put(`/api/products/${product.productId}`, {
        name: product.name,
        price: product.price,
        stock: parseInt(product.stock, 10),
        weight: parseFloat(product.weight),
        description: product.description,
        benefit: parseFloat(product.benefit),
        isFeatured: product.isFeatured || false,
        isBestSeller: product.isBestSeller || false,
        isCustomizable: product.isCustomizable || false,

        sizes: product.sizes,

      });
      toast.success('Product updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update product');
    } finally {
      setSavingRowId(null);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);  // Deselect all
    } else {
      setSelectedIds(filteredProducts.map(p => p._id)); // Select all
    }
    setSelectAll(!selectAll);
  };

  const handleDeleteSelected = async () => {
    try {
      setLoadingDelete(true);
      await axios.delete('/api/products/bulk-delete', {
        data: { ids: selectedIds },
      });
      toast.success('Selected products deleted');
      fetchProducts();
      setSelectedIds([]);  // Clear selection after delete
      setSelectAll(false);
    } catch (error) {
      console.error('Error deleting products:', error);
      toast.error('Failed to delete selected products');
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <div className="px-1 sm:px-6 w-full">
      <h2 className="text-2xl font-bold mb-4">Admin Product Manager</h2>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products"
          className="w-full md:w-1/2 border p-2 rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
          <button
            onClick={() => setHideOutOfStock(!hideOutOfStock)}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-1 px-2 rounded-md shadow-md transition-all duration-200"
          >
            {hideOutOfStock ? 'Show All Products' : 'Hide Out of Stock'}
          </button>
          {selectedIds.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              disabled={loadingDelete}
              className="bg-red-600 text-white px-2 py-1 rounded-md shadow-lg hover:bg-red-700 hover:scale-105 transition-all duration-200"
            >
              {loadingDelete ? <TailSpin height="20" width="20" color="white" /> : 'Delete Selected'}
            </button>
          )}
        </div>
      </div>

      {/* Sort and Filter Section */}
      <div className="flex gap-4 mb-4">
        <select
          value={sortOption}
          onChange={(e) => handleSortChange(e.target.value)}
          className="p-1 border rounded"
        >
          <option value="new">Newest First</option>
          <option value="old">Oldest First</option>
        </select>

        <div className="flex gap-4">
          {['Necklace', 'Bracelet', 'Ring', 'Earring'].map(type => (
            <label key={type} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedProductTypes.includes(type)}
                onChange={() => handleProductTypeChange(type)}
              />
              {type}
            </label>
          ))}
        </div>
      </div>

      {/* Product Table */}

      <div className="overflow-x-auto">
        <table className="min-w-[900px] w-full border text-xs sm:text-sm">        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-1 sm:p-2 border">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
            </th>
            <th className="p-1 sm:p-2 border">Image</th>
            <th className="p-1 sm:p-2 border">Product ID</th>
            <th className="p-1 sm:p-2 border">Name</th>
            <th className="p-1 sm:p-2 border">Price ($)</th>
            <th className="p-1 sm:p-2 border">Stock</th>
            <th className="p-1 sm:p-2 border">Weight (g)</th>
            <th className="p-1 sm:p-2 border">Benefit ($)</th>
            <th className="p-1 sm:p-2 border">Description</th>
            <th className="p-1 sm:p-2 border">Is Featured</th>
            <th className="p-1 sm:p-2 border">Top 10</th>
            <th className="p-1 sm:p-2 border">Customizable</th>
            <th className="p-1 sm:p-2 border">Action</th>

          </tr>
        </thead>
          <tbody>
            {currentProducts.map((product, index) => (
              <tr key={product._id} className="bg-white rounded-lg shadow-lg hover:shadow-2xl transition-all duration-200">
                <td className="p-1 sm:p-2 border">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(product._id)}
                    onChange={() => handleSelect(product._id)}
                  />
                </td>
                <td className="p-1 sm:p-2 border">
                  {product.photos?.[0] && (
                    <img
                      src={`/uploads/${product.photos[0].replace(/\\/g, '/')}`}
                      alt={product.name}
                      className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded cursor-pointer"
                      onClick={() => handleOpenModal(product)}  // Open modal on click
                    />
                  )}
                </td>
                <td className="p-1 sm:p-2 border">{product.productId}</td>
                <td className="p-1 sm:p-2 border">
                  <input
                    type="text"
                    value={product.name}
                    onChange={(e) => handleChange(index, 'name', e.target.value)}
                    className="w-full border p-1 rounded"
                  />
                </td>
                <td className="p-1 sm:p-2 border font-semibold">
                  ${calculatePrice(product, goldRateIQD, usdToIqdRate)}
                </td>
                <td className="p-1 sm:p-2 border">
                  {product.sizes?.map((sizeObj, sizeIndex) => (
                    <div key={sizeIndex} className="flex items-center gap-2 mb-1">
                      <span className="text-xs w-20">{sizeObj.size}</span>
                      <input
                        type="number"
                        className="w-16 border p-1 rounded"
                        value={sizeObj.stock}
                        onChange={(e) => {
                          const updated = [...products];
                          updated[index].sizes[sizeIndex].stock = parseInt(e.target.value);
                          setProducts(updated);
                        }}
                      />
                    </div>
                  ))}
                </td>
                <td className="p-1 sm:p-2 border">
                  <input
                    type="number"
                    value={product.weight || 0}
                    onChange={(e) => handleChange(index, 'weight', e.target.value)}
                    className="w-24 border p-1 rounded"
                  />
                </td>
                <td className="p-1 sm:p-2 border">
                  <input
                    type="number"
                    value={product.benefit || 0}
                    onChange={(e) => handleChange(index, 'benefit', e.target.value)}
                    className="w-24 border p-1 rounded"
                  />
                </td>
                <td className="p-1 sm:p-2 border text-center">
                  <button
                    className="text-blue-600 underline"
                    onClick={() => {
                      setEditingIndex(index);
                      setDescriptionDraft(product.description || '');
                      setDescModalOpen(true);
                    }}
                  >
                    Description
                  </button>
                </td>

                <td className="p-1 sm:p-2 border">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={product.isFeatured || false}
                      onChange={(e) => handleChange(index, 'isFeatured', e.target.checked)}
                    />
                  </label>

                </td>
                <td className="p-1 sm:p-2 border">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={product.isBestSeller || false}
                      onChange={(e) => handleChange(index, 'isBestSeller', e.target.checked)}
                    />
                  </label>

                </td>
                <td className="p-1 sm:p-2 border">
                  <input
                    type="checkbox"
                    checked={product.isCustomizable || false}
                    onChange={(e) => handleChange(index, 'isCustomizable', e.target.checked)}
                  />
                </td>

                <td className="p-1 sm:p-2 border">
                  <button
                    onClick={() => handleSave(product)}
                    disabled={savingRowId === product._id}
                    className="bg-blue-600 text-white px-2 py-1 rounded-md shadow hover:bg-blue-700 hover:scale-105 transition-all duration-200"
                  >
                    {savingRowId === product._id ? (
                      <TailSpin height="20" width="20" color="white" />
                    ) : (
                      'Save'
                    )}
                  </button>

                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Image Modal for Product */}
      {selectedProduct && (
        <ImageModal
          open={openModal}
          onClose={handleCloseModal}
          product={selectedProduct}
          onUpdate={fetchProducts}
        />
      )}


      {descModalOpen && (
        <DescriptionModal
          open={descModalOpen}
          value={descriptionDraft}
          onChange={setDescriptionDraft}
          onClose={() => setDescModalOpen(false)}
          onSave={() => {
            const updated = [...products];
            updated[editingIndex].description = descriptionDraft;
            setProducts(updated);
            setDescModalOpen(false);
            toast.success('Description updated. Click Save to apply.');
          }}
        />
      )}

      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-center mt-4 gap-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          className="px-2 py-1 bg-gray-200 rounded-l"
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="px-2 py-1">{currentPage} / {totalPages}</span>
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          className="px-2 py-1 bg-gray-200 rounded-r"
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminProductList;
